import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useTranslations } from 'hooks/useTranslations';
import { Elabels } from '../helpers';
import { renderStatusCaseFieldColor } from 'pages/CaseManagementPage/helpers';
import { Chips } from 'components/atoms/Chips';

interface ITasksCard {
  data: ITableDataSource;
  handleView?: (args: string) => void;
  handleAssign?: (args: string) => void;
  canAssignCase: (licenceAuthorityUniqueId: string, caseStatusUniqueId: string) => boolean;
}

const TasksCard = ({ data, handleView, handleAssign, canAssignCase }: ITasksCard) => {
  const { t } = useTranslations();

  return (
    <Grid
      container
      sx={{
        border: colors.gray200,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
      }}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(Elabels.caseID)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.caseNumber)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(Elabels.legalEntityName)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.legalName)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(Elabels.taskName)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.taskName)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(Elabels.licencingAuthority)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.licencingAuthority)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(Elabels.createDate)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.createdDate)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(Elabels.status)}
        </Typography>
        <Typography variant="body2">
          <Chips
            text={data.status}
            variant={renderStatusCaseFieldColor({ statusUniqueId: data.statusUniqueId })}
            fullWidth
          />
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(Elabels.assignedTo)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.assignedTo)}</Typography>
      </Grid>

      <Grid
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: `1px solid ${colors.primary100}`,
          p: 2,
        }}
      >
        <Grid size={{ xs: 12, sm: 3 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.button')}:{' '}
          </Typography>
        </Grid>

        <Grid
          size={{ xs: 12, sm: 9 }}
          container
          spacing={1}
          sx={{ justifyContent: 'flex-end' }}
        >
          {handleView && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                label={t('caseManagement.cases.actions.details.label')}
                variant={EButtonVariants.outlined}
                onClick={() => handleView(data.id as string)}
                fullWidth
                id="cases_card_details-button"
                size={EButtonSizes.small}
              />
            </Grid>
          )}
          {handleAssign &&
            canAssignCase(data.licenceAuthorityUniqueId, data.statusUniqueId) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  variant={EButtonVariants.outlined}
                  label={t(Elabels.assignedTo)}
                  onClick={() => {
                    handleAssign(data.id as string);
                  }}
                  fullWidth
                  id="cases_card_activate-button"
                  size={EButtonSizes.small}
                />
              </Grid>
            )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { TasksCard };
